import { useState, useEffect } from "react";
import { CreditCard, QrCode, FileText, Copy, ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCardData, getInstallmentOptions } from "@/lib/api-payment";
import { formatCEP } from "@/lib/cep";
import { formatBRL } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { DocumentInput } from "@/components/DocumentInput";
import { validateDocument, detectDocumentType, sanitizeDocument } from "@/utils/document";
import { useTranslation } from "react-i18next";

interface CheckoutStepPaymentProps {
  subtotal: number;
  platformFee: number;
  total: number;
  onBack: () => void;
  onConfirm: (method: string, cardData?: CreditCardData, installments?: number) => void;
  isProcessing: boolean;
  pixQrCode?: string | null;
  pixQrCodeImage?: string | null;
  boletoUrl?: string | null;
  boletoBarcode?: string | null;
  paymentCreated: boolean;
  awaitingPayment: boolean;
  expiresAt?: string | null;
  payerCpf: string;
  onPayerCpfChange: (cpf: string) => void;
  companyName: string;
  onCompanyNameChange: (companyName: string) => void;
  buyerCep?: string;
  buyerAddressNumber?: string;
  /** Notifica o parent para atualizar o resumo (sidebar) com método/parcelas/juros */
  onPaymentPreviewChange?: (preview: {
    method: string;
    installments: number;
    chargeTotal: number;
    interestAmount: number;
  }) => void;
}

// M09: Luhn algorithm for card validation
function luhnCheck(num: string): boolean {
  const digits = num.replace(/\D/g, "");
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let alternate = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
}

function validateCardExpiry(month: string, year: string): boolean {
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (isNaN(m) || isNaN(y) || m < 1 || m > 12) return false;
  const now = new Date();
  const expiry = new Date(y, m); // first day of next month
  return expiry > now;
}

export function CheckoutStepPayment({
  subtotal,
  platformFee,
  total,
  onBack,
  onConfirm,
  isProcessing,
  pixQrCode,
  pixQrCodeImage,
  boletoUrl,
  boletoBarcode,
  paymentCreated,
  awaitingPayment,
  payerCpf,
  onPayerCpfChange,
  companyName,
  onCompanyNameChange,
  buyerCep,
  buyerAddressNumber,
  onPaymentPreviewChange,
}: CheckoutStepPaymentProps) {
  const { t } = useTranslation("checkout");
  const [paymentMethod, setPaymentMethod] = useState<string>("pix");
  const [installments, setInstallments] = useState<number>(1);
  const [cardData, setCardData] = useState<CreditCardData>({
    holderName: "",
    number: "",
    expiryMonth: "",
    expiryYear: "",
    ccv: "",
    postalCode: "",
    addressNumber: "",
  });

  // Pré-preencher CEP e número com dados do comprador
  useEffect(() => {
    if (paymentMethod === "credit_card" && (buyerCep || buyerAddressNumber)) {
      setCardData((prev) => ({
        ...prev,
        postalCode: buyerCep || prev.postalCode,
        addressNumber: buyerAddressNumber || prev.addressNumber,
      }));
    }
  }, [paymentMethod, buyerCep, buyerAddressNumber]);

  // Boleto/PIX awaiting status (no expiration timer)

  const installmentOptions = getInstallmentOptions(total);
  const selectedInstallment =
    installmentOptions.find((o) => o.n === installments) || installmentOptions[0];
  const effectiveInstallments = selectedInstallment?.n ?? 1;
  const chargeTotal =
    paymentMethod === "credit_card" && selectedInstallment
      ? selectedInstallment.total
      : total;
  const interestAmount = Math.max(0, Number((chargeTotal - total).toFixed(2)));

  // Se a opção atual sumiu (ex.: filtro R$5), volta para a primeira válida
  useEffect(() => {
    if (
      paymentMethod === "credit_card" &&
      installmentOptions.length > 0 &&
      !installmentOptions.some((o) => o.n === installments)
    ) {
      setInstallments(installmentOptions[0].n);
    }
  }, [paymentMethod, installmentOptions, installments]);

  // Atualiza sidebar do pedido
  useEffect(() => {
    onPaymentPreviewChange?.({
      method: paymentMethod,
      installments: paymentMethod === "credit_card" ? effectiveInstallments : 1,
      chargeTotal,
      interestAmount: paymentMethod === "credit_card" ? interestAmount : 0,
    });
  }, [
    paymentMethod,
    effectiveInstallments,
    chargeTotal,
    interestAmount,
    onPaymentPreviewChange,
  ]);

  const handleCopyPix = () => {
    if (pixQrCode) {
      navigator.clipboard.writeText(pixQrCode);
      toast({ title: t("checkout.paymentSection.copyPix") });
    }
  };

  const handleConfirm = () => {
    const documentValidation = validateDocument(payerCpf);
    if (!documentValidation.valid || !documentValidation.type) {
      toast({ title: documentValidation.error || t("checkout.toastsAndErrors.invalidDocumentTitle"), description: t("checkout.toastsAndErrors.fillValidDocument"), variant: "destructive" });
      return;
    }
    if (paymentMethod === "boleto" && documentValidation.type === "cnpj" && !companyName.trim()) {
      toast({ title: t("checkout.toastsAndErrors.companyNameRequired"), description: t("checkout.toastsAndErrors.companyNameBoletoCnpjDesc"), variant: "destructive" });
      return;
    }

    if (paymentMethod === "credit_card") {
      if (!cardData.holderName || !cardData.number || !cardData.expiryMonth || !cardData.expiryYear || !cardData.ccv || !cardData.postalCode || !cardData.addressNumber) {
        toast({ title: t("checkout.toastsAndErrors.fillAllCardFields"), variant: "destructive" });
        return;
      }
      if (!luhnCheck(cardData.number)) {
        toast({ title: t("checkout.toastsAndErrors.invalidCardNumber"), description: t("checkout.toastsAndErrors.checkCardNumber"), variant: "destructive" });
        return;
      }
      if (!validateCardExpiry(cardData.expiryMonth, cardData.expiryYear)) {
        toast({ title: t("checkout.toastsAndErrors.invalidCardExpiry"), description: t("checkout.toastsAndErrors.cardMightBeExpired"), variant: "destructive" });
        return;
      }
      const cvvClean = cardData.ccv.replace(/\D/g, "");
      if (cvvClean.length < 3 || cvvClean.length > 4) {
        toast({ title: t("checkout.toastsAndErrors.invalidCvv"), description: t("checkout.toastsAndErrors.cvvLengthNotice"), variant: "destructive" });
        return;
      }
      onConfirm(paymentMethod, cardData, effectiveInstallments);
    } else {
      onConfirm(paymentMethod);
    }
  };

  // If payment was created and we're awaiting — show status
  if (paymentCreated && awaitingPayment) {
    const expirationWarning = (
      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{t("checkout.paymentSection.awaitingPayment")}</span>
      </div>
    );

    if (paymentMethod === "pix") {
      return (
        <div className="space-y-6">
          <h2 className="font-display text-xl font-bold">{t("checkout.paymentSection.pixTitle")}</h2>
          <div className="p-6 rounded-lg border border-border bg-card text-center space-y-4">
            {pixQrCodeImage ? (
              <img src={`data:image/png;base64,${pixQrCodeImage}`} alt="QR Code PIX" className="w-48 h-48 mx-auto rounded-lg" />
            ) : (
              <div className="w-48 h-48 mx-auto bg-muted rounded-lg flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            <p className="text-sm text-muted-foreground">{t("checkout.paymentSection.pixInstructions")}</p>
            {pixQrCode && (
              <div className="flex items-center gap-2 max-w-md mx-auto">
                <Input value={pixQrCode} readOnly className="text-xs font-mono" />
                <Button variant="outline" size="icon" onClick={handleCopyPix}><Copy className="h-4 w-4" /></Button>
              </div>
            )}
            {expirationWarning}
          </div>
          <div className="p-4 rounded-lg border border-border bg-card space-y-2 text-sm">
            <div className="flex justify-between font-semibold"><span>{t("cart.total")}</span><span>{formatBRL(total)}</span></div>
          </div>
        </div>
      );
    }

    if (paymentMethod === "boleto") {
      return (
        <div className="space-y-6">
          <h2 className="font-display text-xl font-bold">{t("checkout.paymentSection.boletoTitle")}</h2>
          <div className="p-6 rounded-lg border border-border bg-card space-y-4">
            <div className="text-center space-y-3">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground" />
              <p className="text-sm font-medium">{t("checkout.paymentSection.boletoGenerated")}</p>
              <p className="text-xs text-muted-foreground">{t("checkout.paymentSection.boletoNotice")}</p>
            </div>
            {boletoUrl && (
              <Button variant="outline" className="w-full" onClick={() => window.open(boletoUrl, "_blank")}>
                <ExternalLink className="h-4 w-4 mr-2" />{t("checkout.paymentSection.openBoletoPdf")}
              </Button>
            )}
            {boletoBarcode && (
              <div className="space-y-2">
                <Label className="text-sm">{t("checkout.paymentSection.barcode")}</Label>
                <div className="flex items-center gap-2">
                  <Input value={boletoBarcode} readOnly className="text-xs font-mono" />
                  <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(boletoBarcode); toast({ title: t("checkout.paymentSection.barcodeCopied") }); }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            {expirationWarning}
          </div>
        </div>
      );
    }
  }

  const documentType = detectDocumentType(sanitizeDocument(payerCpf));

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <h2 className="font-display text-2xl font-bold text-foreground">{t("checkout.paymentSection.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("checkout.paymentSection.subtitle")}</p>
      </div>

      {/* CPF do pagador */}
      <div className="p-4 rounded-lg border border-border bg-card space-y-2">
        <DocumentInput
          label={t("checkout.paymentSection.payerDocLabel")}
          value={payerCpf}
          onChange={onPayerCpfChange}
          required
        />
        {documentType === "cnpj" && paymentMethod === "boleto" && (
          <div className="pt-2">
            <Label className="text-sm">{t("checkout.paymentSection.companyNameLabel")}</Label>
            <Input
              placeholder={t("checkout.paymentSection.companyNamePlaceholder")}
              value={companyName}
              onChange={(e) => onCompanyNameChange(e.target.value.slice(0, 100))}
              maxLength={100}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("checkout.paymentSection.companyNameBoletoNotice")}
            </p>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {t("checkout.paymentSection.payerDocNotice")}
        </p>
      </div>

      <div className="space-y-2">
        {[
          { id: "pix", label: t("checkout.paymentSection.pixLabel"), icon: QrCode, desc: t("checkout.paymentSection.pixDesc") },
          { id: "credit_card", label: t("checkout.paymentSection.cardLabel"), icon: CreditCard, desc: t("checkout.paymentSection.cardDesc") },
          { id: "boleto", label: t("checkout.paymentSection.boletoLabel"), icon: FileText, desc: t("checkout.paymentSection.boletoDesc") },
        ].map((m) => (
          <button
            key={m.id}
            onClick={() => {
              setPaymentMethod(m.id);
              if (m.id !== "credit_card") setInstallments(1);
            }}
            disabled={isProcessing}
            className={`w-full flex items-center gap-3 p-4 rounded-lg border transition-colors ${
              paymentMethod === m.id ? "border-primary bg-primary/5" : "border-border bg-card hover:border-muted-foreground/30"
            }`}
          >
            <m.icon className="h-5 w-5 text-muted-foreground" />
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">{m.label}</p>
              <p className="text-xs text-muted-foreground">{m.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {paymentMethod === "credit_card" && (
        <div className="p-4 rounded-lg border border-border bg-card space-y-3">
          <div>
            <Label className="text-sm">{t("checkout.paymentSection.cardNumber")}</Label>
            <Input placeholder="0000 0000 0000 0000" value={cardData.number} onChange={(e) => setCardData((p) => ({ ...p, number: e.target.value }))} maxLength={19} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">{t("checkout.paymentSection.month")}</Label>
              <Input placeholder="MM" value={cardData.expiryMonth} onChange={(e) => setCardData((p) => ({ ...p, expiryMonth: e.target.value }))} maxLength={2} />
            </div>
            <div>
              <Label className="text-sm">{t("checkout.paymentSection.year")}</Label>
              <Input placeholder="AAAA" value={cardData.expiryYear} onChange={(e) => setCardData((p) => ({ ...p, expiryYear: e.target.value }))} maxLength={4} />
            </div>
          </div>
          <div>
            <Label className="text-sm">{t("checkout.paymentSection.cvv")}</Label>
            <Input placeholder="000" value={cardData.ccv} onChange={(e) => setCardData((p) => ({ ...p, ccv: e.target.value }))} maxLength={4} />
          </div>
          <div>
            <Label className="text-sm">{t("checkout.paymentSection.cardHolder")}</Label>
            <Input placeholder={t("checkout.paymentSection.cardHolderPlaceholder")} value={cardData.holderName} onChange={(e) => setCardData((p) => ({ ...p, holderName: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">{t("checkout.paymentSection.postalCode")}</Label>
              <Input placeholder="00000-000" value={cardData.postalCode || ""} onChange={(e) => setCardData((p) => ({ ...p, postalCode: formatCEP(e.target.value) }))} maxLength={9} />
            </div>
            <div>
              <Label className="text-sm">{t("checkout.paymentSection.addressNumber")}</Label>
              <Input placeholder="123" value={cardData.addressNumber || ""} onChange={(e) => setCardData((p) => ({ ...p, addressNumber: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="text-sm">{t("checkout.paymentSection.installments")}</Label>
            <Select
              value={String(effectiveInstallments)}
              onValueChange={(v) => setInstallments(Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {installmentOptions.map((opt) => (
                  <SelectItem key={opt.n} value={String(opt.n)}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {installmentOptions.length === 1 && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("checkout.paymentSection.installmentsUnavailable")}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="p-4 rounded-lg border border-border bg-card space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">{t("cart.subtotal")}</span><span>{formatBRL(subtotal)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">{t("cart.serviceFee")}</span><span>{formatBRL(platformFee)}</span></div>
        {paymentMethod === "credit_card" && interestAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("checkout.paymentSection.cardInterest")}</span>
            <span>{formatBRL(interestAmount)}</span>
          </div>
        )}
        {paymentMethod === "credit_card" && effectiveInstallments > 1 && selectedInstallment && (
          <div className="flex justify-between text-muted-foreground text-xs">
            <span>{t("checkout.sidebar.installments")}</span>
            <span>{effectiveInstallments}x de {formatBRL(selectedInstallment.value)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold border-t border-border pt-2">
          <span>{t("cart.total")}</span>
          <span>{formatBRL(chargeTotal)}</span>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        {t("checkout.paymentSection.termsAgreement")}{" "}
        <Link to="/termos-de-uso/cliente" className="text-primary hover:underline">{t("checkout.paymentSection.termsLinkText")}</Link> e a <Link to="/politica-de-privacidade" className="text-primary hover:underline">{t("checkout.paymentSection.privacyLinkText")}</Link> da Ticket Hall.
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1" disabled={isProcessing}>{t("checkout.paymentSection.back")}</Button>
        <Button onClick={handleConfirm} className="flex-1" disabled={isProcessing}>
          {isProcessing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("checkout.paymentSection.processing")}</>) : t("checkout.paymentSection.confirmPayment")}
        </Button>
      </div>
    </div>
  );
}
